import { createAction, Middleware } from '@reduxjs/toolkit';
import RobotWebsocketMessage from '@common/interfaces/robot/robot-websocket-message';
import { handleWebsocketMessage, setRobotHubConnectionStatus } from 'store/robots/robots-slice';
import { handleWebsocketMessage as handleLocalRobotWebsocketMessage } from 'store/local-robot/local-robot-slice';
import WebSocketConnectionStatus from 'models/enums/websocket-connection-status';
import {
  baseWebsocketUrl,
  websocketProtocols,
  WS_CLOSE_FORBIDDEN,
  WS_CLOSE_UNAUTHENTICATED,
} from 'services/data/axios-base';
import MessageType from '@common/enums/robot/rsw/message-types';
import WebSocketMessageType from 'models/enums/websocket-message-type';
import { trackEvent } from 'services/analytics';
import { AnalyticsEvent, CommandOutcome } from 'services/analytics-events';

/** How long to wait for the robot to answer a command before reporting it as timed out. */
export const COMMAND_RESPONSE_TIMEOUT_MS = 30_000;

// Commands whose outcome is tracked, and the inbound message that shows the robot answered.
const COMMAND_RESPONSE_TYPES: Partial<Record<MessageType, WebSocketMessageType>> = {
  [MessageType.Actions]: WebSocketMessageType.ACTIONS,
  [MessageType.GetLiveStatus]: WebSocketMessageType.CLEANING_IMAGE,
};

interface PendingCommand {
  commandType: MessageType;
  commandAction: string | null;
  robotId: string;
  robotSerial: string | null;
  sentAt: number;
  timer: ReturnType<typeof setTimeout>;
}

export const sendRobotWebsocketMessage = createAction<RobotWebsocketMessage>('robot/sendMessage');
export const connectRobotWebsocket = createAction<string>('robot/connectWebsocket');
export const disconnectRobotWebsocket = createAction('robot/disconnectWebsocket');

export const robotWebsocketMiddleware: Middleware = (store) => {
  let socket: WebSocket | null = null;
  let robotId: string | null = null;
  let openedAt: number | null = null;
  const pendingCommands = new Map<MessageType, PendingCommand>();

  const getRobotSerial = (): string | null =>
    store.getState()?.robots?.robots?.currentRobot?.serialNumber ?? null;

  const reportCommand = (
    commandType: MessageType,
    commandAction: string | null,
    outcome: CommandOutcome,
    latencyMs: number | null,
    commandRobotId: string | null,
    robotSerial: string | null,
  ) => {
    trackEvent(AnalyticsEvent.RobotCommandCompleted, {
      robot_id: commandRobotId,
      robot_serial: robotSerial,
      command_type: commandType,
      command_action: commandAction,
      outcome,
      latency_ms: latencyMs,
    });
  };

  const settleCommand = (commandType: MessageType, outcome: CommandOutcome) => {
    const pending = pendingCommands.get(commandType);
    if (pending == null) return;
    clearTimeout(pending.timer);
    pendingCommands.delete(commandType);
    reportCommand(
      pending.commandType,
      pending.commandAction,
      outcome,
      Date.now() - pending.sentAt,
      pending.robotId,
      pending.robotSerial,
    );
  };

  // Commands still waiting when the page switches robot are not answered on the new socket;
  // they are dropped rather than reported as timeouts.
  const dropPendingCommands = () => {
    pendingCommands.forEach((pending) => clearTimeout(pending.timer));
    pendingCommands.clear();
  };

  const trackSentCommand = (payload: RobotWebsocketMessage, sentRobotId: string) => {
    const commandType = payload.type as MessageType;
    if (COMMAND_RESPONSE_TYPES[commandType] == null) return;
    // Repeated clicks while a command is outstanding are measured from the first one.
    if (pendingCommands.has(commandType)) return;
    const message = payload.message as { action?: unknown } | null;
    pendingCommands.set(commandType, {
      commandType,
      commandAction: typeof message?.action === 'string' ? message.action : null,
      robotId: sentRobotId,
      robotSerial: getRobotSerial(),
      sentAt: Date.now(),
      timer: setTimeout(
        () => settleCommand(commandType, CommandOutcome.TimedOut),
        COMMAND_RESPONSE_TIMEOUT_MS,
      ),
    });
  };

  const settleByResponse = (responseType: unknown) => {
    (Object.keys(COMMAND_RESPONSE_TYPES) as MessageType[]).forEach((commandType) => {
      if (COMMAND_RESPONSE_TYPES[commandType] === responseType) {
        settleCommand(commandType, CommandOutcome.Responded);
      }
    });
  };

  return (next) => (action) => {
    if (connectRobotWebsocket.match(action)) {
      if (robotId === action.payload && socket && socket.readyState === WebSocket.OPEN) return;
      if (socket) socket.close();
      if (robotId !== action.payload) dropPendingCommands();
      openedAt = null;

      const thisSocket = new WebSocket(
        `${baseWebsocketUrl}/robot/${action.payload}/`,
        websocketProtocols(),
      );
      const thisRobotId = action.payload;
      socket = thisSocket;
      robotId = thisRobotId;

      const isCurrent = () => socket === thisSocket && robotId === thisRobotId;

      thisSocket.onopen = () => {
        if (!isCurrent()) return;
        openedAt = Date.now();
        store.dispatch(setRobotHubConnectionStatus(WebSocketConnectionStatus.CONNECTED));
      };

      thisSocket.onclose = (event) => {
        if (!isCurrent()) return;
        store.dispatch(setRobotHubConnectionStatus(WebSocketConnectionStatus.DISCONNECTED));
        // Only a drop of an established connection is reported, not each failed reconnect attempt.
        if (openedAt != null) {
          trackEvent(AnalyticsEvent.RobotConnectionLost, {
            robot_id: thisRobotId,
            close_code: event.code,
            connected_ms: Date.now() - openedAt,
          });
          openedAt = null;
        }
        if (event.code === WS_CLOSE_UNAUTHENTICATED || event.code === WS_CLOSE_FORBIDDEN) {
          robotId = null;
          return;
        }
        // Try to reconnect
        if (robotId !== null) {
          setTimeout(() => {
            store.dispatch(connectRobotWebsocket(robotId!));
          }, 1000);
        }
      };

      thisSocket.onerror = (error) => {
        console.log('Websocket error:', error);
      };

      thisSocket.onmessage = (event) => {
        if (!isCurrent()) return;
        try {
          const data = JSON.parse(event.data);
          settleByResponse(data.message?.type);
          store.dispatch(handleWebsocketMessage(data.message));
          store.dispatch(handleLocalRobotWebsocketMessage(data.message));
        } catch (err) {
          console.error('WebSocket message parse error', err);
        }
      };
    }

    if (disconnectRobotWebsocket.match(action)) {
      socket?.close();
      socket = null;
      robotId = null;
      openedAt = null;
      dropPendingCommands();
    }

    if (sendRobotWebsocketMessage.match(action)) {
      if (socket && robotId && socket.readyState === WebSocket.OPEN) {
        action.payload.robot_id = robotId;
        socket.send(JSON.stringify(action.payload));
        trackSentCommand(action.payload, robotId);
      } else {
        console.warn('WebSocket not connected, message not sent:', action.payload);
        const commandType = action.payload.type as MessageType;
        if (COMMAND_RESPONSE_TYPES[commandType] != null) {
          const message = action.payload.message as { action?: unknown } | null;
          reportCommand(
            commandType,
            typeof message?.action === 'string' ? message.action : null,
            CommandOutcome.NotSent,
            null,
            robotId,
            getRobotSerial(),
          );
        }
      }
    }

    return next(action);
  };
};
